<template>
  <AppDialog :is-open="isDialogOpened"
             size="l"
             :ok-text="$t('subscriptions.views.SubscriptionConsumption.ShareDialog.share')"
             :okLoading="okLoading"
             @cancel="closeDialog"
             @ok="submitForm"
  >
    <template #title>
      <p class="text--large text--bold">
        {{ title }}
      </p>
    </template>

    <template #body>
      <div>
        <div class="mb-6">
          <p class="text--small text--bold">
            {{ $t('subscriptions.views.SubscriptionConsumption.ShareDialog.emailAddresses') }}
          </p>
          <v-combobox v-model="emailAddresses"
                      multiple
                      clearable
                      density="compact"
                      variant="outlined"
                      color="blue-darken-2"
                      :clear-on-select="false"
                      :placeholder="$t('subscriptions.views.SubscriptionConsumption.ShareDialog.emailAddressesPlaceholder')"
                      @update:search="onEmailAddressesInputChange"
                      @update:modelValue="validateEmails"
                      :error-messages="emailAddressesErrors"
                      @blur="v$.emailAddressesInput.$touch"
                      ref="emailAddressesCombobox"
          >
            <template v-slot:selection="data">
              <v-chip
                closable
                @click:close="removeEmailAddress(data.index)"
                size="small"
              >
                <template v-slot:prepend>
                  <app-icon icon-name="envelope" size="small" icon-weight="fal" class="mr-1"/>
                </template>
                {{ data.item.title }}
              </v-chip>
            </template>

            <template v-if="emailAddressesInput" #append-item>
              <div class="px-5 py-2 d-flex align-center cursor-pointer inputPreview" @click="addEmailAddress">
                <app-icon icon-name="envelope" icon-weight="fal" class="mr-2 text-grey-600" />
                <div class="text-grey-900">{{ emailAddressesInput }}</div>
              </div>
            </template>
          </v-combobox>
        </div>

        <div class="mb-6">
          <p class="text--small text--bold">
            {{ $t('subscriptions.views.SubscriptionConsumption.ShareDialog.customMessage') }}
          </p>
          <v-textarea v-model="customMessage"
                      color="blue-darken-2"
                      counter="200"
                      persistent-counter
                      variant="outlined"
                      :placeholder="$t('subscriptions.views.SubscriptionConsumption.ShareDialog.customMessagePlaceholder')"
                      rows="4"
                      :error-messages="customMessageErrors"
          />
        </div>
      </div>
    </template>
  </AppDialog>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import { helpers, maxLength, required} from "@vuelidate/validators";

import AppDialog from '@/common/dialogs/AppDialog.vue'
import {formFieldErrors} from "@/common/utils/form";
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { postmarkEmail } from '@/validators/postmarkEmail'

export default {
  name: 'ShareSubscriptionConsumptionDialog',
  components: { AppDialog },
  mixins: [dialogMixin],
  setup: () => ({ v$: useVuelidate() }),
  props: {
    title: {
      type: String,
      required: true,
    },
    submitAction: {
      type: Function,
      required: true,
    },
    actionParams: {
      type: Object,
      default: () => ({}),
    },
    okLoading: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      emailAddresses: [],
      emailAddressesInput: '',
      customMessage: '',
    }
  },
  validations() {
    return {
      emailAddresses: {
        required: required,
        $each: helpers.forEach({
          postmarkEmail,
        }),
      },
      emailAddressesInput: {
        postmarkEmail,
      },
      customMessage: {
        maxLengthValue: maxLength(200),
      },
    }
  },
  computed: {
    emailAddressesErrors() {
      const requiredConstraintErrors = formFieldErrors(
        this.v$.emailAddresses,
        [
          [
            'required',
            this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.noEmailAddressError'),
          ],
        ],
      )

      const emailInputErrors =  formFieldErrors(
        this.v$.emailAddressesInput,
        [
          [
            'postmarkEmail',
            this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.invalidEmail'),
          ],
        ],
      )

      return [...requiredConstraintErrors, ...emailInputErrors]
    },
    customMessageErrors () {
      return formFieldErrors(
        this.v$.customMessage,
        [
          [
            'maxLengthValue',
            this.$t('common.validations.textTooLong'),
          ],
        ],
      )
    },
  },
  methods: {
    validateEmails(inputEmails) {
      const addedEmail = inputEmails[inputEmails.length - 1]
      if (!postmarkEmail(addedEmail)) {
        inputEmails.pop()
      } else {
        this.$refs.emailAddressesCombobox.search = ''
      }
    },
    removeEmailAddress(index) {
      this.emailAddresses.splice(index, 1)
    },
    onEmailAddressesInputChange (newInput) {
      this.emailAddressesInput = newInput
    },
    addEmailAddress () {
      if (!this.emailAddressesInput) {
        return
      }

      if (!postmarkEmail(this.emailAddressesInput)) {
        this.v$.emailAddressesInput.$touch()
        return
      }

      this.emailAddresses.push(this.emailAddressesInput)

      this.$refs.emailAddressesCombobox.search = ''
    },
    async submitForm () {
      this.v$.$touch()
      if (!this.v$.$invalid) {

        try {
          await this.submitAction({
            ...this.actionParams,
            data: {
              emailAddresses: this.emailAddresses,
              customMessage: this.customMessage,
            },
          })

          this.closeDialog()
          toast.success(this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.successMessage'))
        } catch (e) {
          console.error(e)
          toast.error(this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.errorMessage'))
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.inputPreview:hover {
  background-color: rgb(var(--v-theme-grey-50));
}
</style>
